/**
 * The owl above the sign-in pages (the "owl-hero" artboard in apps/web/rive). Its brow lights run a
 * slow show; it looks down at the form while someone types, shuts its eyes while a password is
 * typed (and opens them again if they choose to show it), shakes its head when something goes wrong
 * and flashes its lights when someone gets in.
 */
import { useEffect, useSyncExternalStore, type FocusEvent, type ReactNode } from "react";
import { RiveArt } from "@/components/rive/RiveArt";
import { BrandMark } from "@/components/shell/BrandMark";
import styles from "./auth.module.css";

/**
 * What the owl reacts to. A small store rather than context: the forms that drive it render the
 * layout the owl sits in, so they're above it, not inside it.
 */
interface OwlState {
  field: "password" | "typing" | null;
  showing: boolean;
  cheers: number;
  frowns: number;
}

let state: OwlState = { field: null, showing: false, cheers: 0, frowns: 0 };
const listeners = new Set<() => void>();
function set(next: Partial<OwlState>) {
  if (Object.entries(next).every(([k, v]) => state[k as keyof OwlState] === v)) return;
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

const owl = {
  /** What has focus: a password being typed, or another field. */
  focus: (field: OwlState["field"]) => set({ field }),
  success: () => set({ cheers: state.cheers + 1 }),
  error: () => set({ frowns: state.frowns + 1 }),
};

export const useOwl = () => owl;

/** Focus handlers for a form, so the owl follows what's being typed. Pass whether the password is showing. */
export function useOwlForm(showingPassword = false) {
  useEffect(() => set({ showing: showingPassword }), [showingPassword]);
  useEffect(() => () => set({ field: null }), []);
  return {
    onFocus: (e: FocusEvent<HTMLFormElement>) => {
      const field = e.target;
      if (!(field instanceof HTMLInputElement)) return owl.focus(null);
      owl.focus(/password/.test(field.autocomplete) ? "password" : "typing");
    },
    onBlur: (e: FocusEvent<HTMLFormElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) owl.focus(null);
    },
  };
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function OwlHero({ children }: { children: ReactNode }) {
  const { field, showing, cheers, frowns } = useSyncExternalStore(subscribe, () => state, () => state);
  const peek = field === "password" && !showing;
  return (
    <>
      <div className={styles.hero}>
        <RiveArt
          artboard="owl-hero"
          width={176}
          height={176}
          inputs={{ peek, look: field !== null && !peek, success: cheers, error: frowns }}
          fallback={<BrandMark size={84} />}
        />
        <div className={styles.wordmark}>
          <span className={styles.wordmarkName}>Night Owl</span>
          <span className={styles.wordmarkLine}>Permanent Lighting</span>
        </div>
      </div>
      {children}
    </>
  );
}
